// De CSV met onderzoeksresultaten (Instellingen → Data): één regel per
// ingevulde vragenlijst, één kolom per vraag, en bij een matrixvraag één kolom
// per stelling.
//
// Deze aggregatie stond in het scherm zelf en was daardoor niet te toetsen.
// Ze staat hier apart omdat hij twee keer stil antwoorden liet vallen:
//
// 1. De kolom werd gesleuteld op de TÉKST van de stelling. Twee matrixvragen in
//    dezelfde vragenlijst kunnen dezelfde stelling bevatten ('voor' en 'na'),
//    en dan viel de tweede over de eerste heen.
//    AI-CONTEXT: See docs/pitfalls.md#database-rowtitle-join-strands-renamed-rows
// 2. Een matrixantwoord zonder rij-ID werd overgeslagen, ook als de rijtitel er
//    gewoon stond.
//
// Bij één klant kostte dat samen 304 van de 2.977 antwoorden, in een bestand
// dat in een verantwoording terechtkomt.
//
// De groepering per milliseconde-tijdstempel is bewust ongewijzigd: zo bepalen
// alle antwoordweergaven wat 'één invulling' is.
// AI-CONTEXT: See docs/pitfalls.md#database-responses-grouped-by-millisecond-timestamp

/**
 * @typedef {object} FieldMeta
 * @property {string} title - De vraagtekst
 * @property {number} position - Volgnummer in de vragenlijst
 */

/**
 * @typedef {object} MatrixRowSet
 * @property {Record<string, {Title: string, Position: number}>} byId - Stellingen op hun ID
 */

/**
 * @typedef {object} ResearchResultRows
 * @property {string[]} headerOrder - Kolomkoppen op volgorde, te beginnen met Date en Moment
 * @property {Array<Record<string, string>>} rows - Eén object per ingevulde vragenlijst
 * @property {string[]} skipped - ID's van antwoorden die nergens te plaatsen waren
 */

/** Wat elke regel voorop draagt. */
const FIXED_COLUMNS = ['Date', 'Moment']

/**
 * @param {object} params
 * @param {Array<object>} params.responses - Documenten uit QuestionnairesResponses
 * @param {Map<string, FieldMeta>} params.fieldMeta - Vraagdefinitie per FieldID
 * @param {Map<string, MatrixRowSet>} [params.matrixRows] - Stellingen per FieldID
 * @param {(momentId: string) => string} params.momentTitle - Titel van een meetmoment
 * @returns {ResearchResultRows}
 */
const buildResearchResultRows = ({ responses, fieldMeta, matrixRows, momentTitle }) => {
    const rowsByTimestamp = {}
    /** @type {Record<string, {label: string, questionTitle: string, order: number}>} */
    const columns = {}
    const skipped = []

    for (const doc of responses || []) {
        const date = doc.Timestamp?.toDate?.()
        const key = date?.toISOString?.() || ''

        if (!rowsByTimestamp[key]) {
            rowsByTimestamp[key] = {
                Date: date?.toLocaleString?.('nl-NL') || '',
                Moment: '',
                cells: {},
            }
        }

        // Eén keer per invulling zetten: anders bepaalt de volgorde waarin
        // Firestore de documenten teruggeeft welk meetmoment er komt te staan.
        const moment = momentTitle(doc.MomentID)
        if (moment && !rowsByTimestamp[key].Moment) rowsByTimestamp[key].Moment = moment

        const fieldId = doc.FieldID
        if (!fieldId) continue

        const field = fieldMeta.get(fieldId) || { title: '', position: 0 }
        const isMatrix = doc.FieldType === 'matrix-one' || doc.FieldType === 'matrix-multiple'

        if (!isMatrix) {
            const columnKey = `f:${fieldId}`
            columns[columnKey] = {
                label: field.title || fieldId,
                questionTitle: field.title || fieldId,
                order: field.position || 0,
            }
            rowsByTimestamp[key].cells[columnKey] = doc.Input
            continue
        }

        // Het rij-ID heeft in de loop der jaren drie namen gedragen. Ontbreekt
        // het alle drie, dan is de rijtitel nog altijd genoeg om het antwoord
        // een eigen kolom te geven — beter dan het weggooien.
        const rowId = doc.MatrixRowID || doc.RowID || doc.MatrixRow || null
        const rowTitle = doc.RowTitle || ''
        if (!rowId && !rowTitle) {
            skipped.push(doc.ID || '(onbekend document)')
            continue
        }

        const knownRow = rowId ? matrixRows?.get(fieldId)?.byId?.[rowId] : null
        const label = knownRow?.Title || rowTitle || `Rij ${rowId}`

        // Sleutel op veld + rij, nooit op de tekst: twee stellingen mogen
        // hetzelfde heten zonder elkaar te overschrijven.
        const columnKey = `m:${fieldId}:${rowId || `titel:${rowTitle}`}`
        columns[columnKey] = {
            label,
            questionTitle: field.title || fieldId,
            // Vast getal in plaats van een kommagetal: 1.10 hoort ná 1.2.
            order: (field.position || 0) * 10000 + (knownRow?.Position || 0),
        }
        rowsByTimestamp[key].cells[columnKey] = doc.Input
    }

    const ordered = Object.entries(columns).sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0))

    // Een kop moet de kolom aanwijzen. Botsen er twee, dan zegt de vraag
    // waar de stelling bij hoort welke welke is.
    const labelCount = {}
    for (const [, column] of ordered) {
        labelCount[column.label] = (labelCount[column.label] || 0) + 1
    }
    const headerByKey = {}
    const used = new Set(FIXED_COLUMNS)
    for (const [columnKey, column] of ordered) {
        let header = labelCount[column.label] > 1
            ? `${column.label} (${column.questionTitle})`
            : column.label
        // Ook dát kan nog botsen; dan telt een nummer ze uit elkaar, want twee
        // gelijke sleutels in één regel is weer één verdwenen antwoord.
        let n = 2
        const base = header
        while (used.has(header)) header = `${base} ${n++}`
        used.add(header)
        headerByKey[columnKey] = header
    }

    const headerOrder = [...FIXED_COLUMNS, ...ordered.map(([columnKey]) => headerByKey[columnKey])]

    const rows = Object.values(rowsByTimestamp).map((entry) => {
        const row = { Date: entry.Date, Moment: entry.Moment }
        for (const [columnKey] of ordered) {
            row[headerByKey[columnKey]] = entry.cells[columnKey] ?? ''
        }
        return row
    })

    return { headerOrder, rows, skipped }
}

export default buildResearchResultRows
